import { ServiceStatus, ServiceStatusRdn, ServiceStatusRollup } from '@chr/online-shared-ui-constants';

import { BookingService, LocationInfo, ShipmentStop, StopActivity } from '@/common/apis/dtos';
import { BookingStatusLabels, StopActivityDateKey, StopType, TransportMode } from '@/common/constants/booking-types';

export const getServiceStops = (services?: BookingService[]): LocationInfo[] => {
  if (!services || services.length === 0) {
    return [];
  }

  return [{ ...services[0].pickupFrom }, { ...services[0].deliverTo }];
};

export const getSortedStops = (serviceStops: LocationInfo[], shipmentStops?: ShipmentStop[]): ShipmentStop[] => {
  // Convert and use Service stops (origin && dest) if shipment stops not available
  if (!shipmentStops || shipmentStops.length === 0) {
    return serviceStops.map(((s, i) => ({
        type: i === 0 ? StopType['Pickup From'] : StopType['Deliver To'],
        topNumber: i,
        location: { ...s.location },
        activities: [{ requestedDateTime: s.requestedDate }]
      })
    ));
  }

  return shipmentStops.sort((stopA, stopB) => (stopA.stopNumber || 0) - (stopB?.stopNumber || 0));
};

export const getTransportModeFromDesc = (modeDescription?: string): TransportMode => {
  if (!modeDescription) {
    return TransportMode.NONE;
  }

  if (modeDescription.trim().toLowerCase() === 'air') {
    return TransportMode.AIR;
  }

  if (modeDescription.trim().toLowerCase() === 'ocean') {
    return TransportMode.OCEAN;
  }

  return TransportMode.NONE;
};

export const getRollupBookingStatusLabel = (statusId: ServiceStatusRdn): string | null => {
  const serviceStatusLookup = Object.keys(ServiceStatusRollup).reduce((acc, status) => {
    ServiceStatusRollup[status as ServiceStatus].forEach((value) => (acc[value] = status));
    return acc;
  }, {} as any) as Record<ServiceStatusRdn, ServiceStatus>;

  const bookingStatus = serviceStatusLookup[statusId];
  return bookingStatus ? BookingStatusLabels[bookingStatus] : null;
};

// Calculate the max date given a date property that exists on the StopActivity object
export const getMaxActivityDate = (activities: StopActivity[], propertyName: StopActivityDateKey) => {
  if (activities.length === 0) {
    return '';
  }

  const defaultDateStr = '1900-01-01';

  const maxDateStr = activities.reduce((maxDate, activity) => {
    let date;

    // There are two special dates that belong to the Appointment object nested in the StopActivity object
    if (propertyName === 'openDateTime' || propertyName === 'closeDateTime') {
      date = activity.appointment?.[propertyName] ?? defaultDateStr;
    } else {
      date = activity[propertyName] ?? defaultDateStr
    }

    return date > maxDate ? date : maxDate;
  }, defaultDateStr);

  return maxDateStr !== defaultDateStr ? maxDateStr : '';
}
